<script setup lang="ts">
import { UserRound } from '@lucide/vue'
import { Avatar, AvatarFallback, AvatarImage } from '@zerohuman/ui'
import type { HTMLAttributes } from 'vue'
import { ref } from 'vue'
import type { Member } from '@/api'
import { initials } from '@/lib/avatar'
import { cn } from '@/lib/utils'

defineOptions({ name: 'MemberAvatar' })

const props = defineProps<{
    member: Pick<Member, 'name' | 'avatarUrl'>
    class?: HTMLAttributes['class']
    /** Initials size, e.g. `text-2xl` on a large avatar. */
    fallbackClass?: HTMLAttributes['class']
}>()

const open = ref(false)
const previewStyle = ref<Record<string, string>>({})

function showPreview(event: PointerEvent): void {
    const box = (event.currentTarget as HTMLElement).getBoundingClientRect()
    previewStyle.value = {
        left: `${Math.round(box.left + box.width / 2)}px`,
        top: `${Math.round(box.top - 8)}px`,
    }
    open.value = true
}

function hidePreview(): void {
    open.value = false
}
</script>

<template>
	<span
		:class="cn('inline-flex shrink-0', props.class)"
		data-testid="member-avatar"
		@pointerenter="showPreview"
		@pointerleave="hidePreview"
	>
		<Avatar class="size-full">
			<AvatarImage v-if="member.avatarUrl" :src="member.avatarUrl" :alt="member.name" />
			<AvatarFallback :class="cn('font-medium', fallbackClass)" :aria-label="member.name">
				<template v-if="initials(member.name)">{{ initials(member.name) }}</template>
				<UserRound v-else class="size-1/2" aria-hidden="true" />
			</AvatarFallback>
		</Avatar>
		<Teleport to="body">
			<div
				v-if="open"
				data-testid="member-avatar-preview"
				role="tooltip"
				class="bg-foreground text-background pointer-events-none fixed z-[80] flex -translate-x-1/2 -translate-y-full flex-col items-center gap-2 rounded-md p-2 shadow-lg"
				:style="previewStyle"
			>
				<Avatar class="size-32">
					<AvatarImage v-if="member.avatarUrl" :src="member.avatarUrl" :alt="member.name" />
					<AvatarFallback :class="cn('text-4xl font-medium')" :aria-label="member.name">
						<template v-if="initials(member.name)">{{ initials(member.name) }}</template>
						<UserRound v-else class="size-1/2" aria-hidden="true" />
					</AvatarFallback>
				</Avatar>
				<span class="max-w-40 truncate text-center text-xs font-medium">{{ member.name }}</span>
			</div>
		</Teleport>
	</span>
</template>
